import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { CalendarDays,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Clock,
  Mic,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { startOfWeek, endOfWeek, addWeeks, subWeeks, format, addDays, getDay } from 'date-fns';
import { EHR_PRE_LOCALIZED_SYSTEMS, formatEhrTime } from '@/utils/dateFormat';
import { encounterService } from '@/services/encounter';
import { appointmentService } from '@/services/appointment';
import { adaptEncounters } from '@/components/encounters/adapters/encounterAdapter';
import { adaptAppointments } from '@/components/encounters/adapters/appointmentAdapter';
import type { UnifiedEncounterItem } from '@/components/encounters/types';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { SubscriptionStatusBar } from '@/components/shared/SubscriptionStatusBar';
import { DataRetentionBanner } from '@/components/shared/DataRetentionBanner';

// ============================================================================
// Provider Homepage / Cockpit
// ----------------------------------------------------------------------------
// One provider homepage for BOTH operating modes. It renders the shared
// UnifiedEncounterItem worklist and swaps only the data source by mode:
//   - connected  → encounters (encounterService + adaptEncounters)
//   - standalone → appointments (appointmentService + adaptAppointments)
// The component name is kept for import stability; it is no longer connected-only.
// ============================================================================

// ============================================================================
// Types
// ============================================================================

interface DaySchedule {
  label: string;
  date: Date;
  dateStr: string;
  items: UnifiedEncounterItem[];
  isToday: boolean;
}

interface DashboardStats {
  total: number;
  completed: number;
  pending: number;
}

// ============================================================================
// Pure helpers (mode-agnostic; operate on UnifiedEncounterItem)
// ============================================================================

// EHR-aware day key: connected encounters from pre-localized EHRs are already in
// their local wall-clock, so bucket them by UTC parts; everything else (including
// all standalone appointments, which carry no externalSystem) uses local date.
function itemDateStr(item: UnifiedEncounterItem): string {
  const d = item.scheduledDate;
  if (item.externalSystem && EHR_PRE_LOCALIZED_SYSTEMS.has(item.externalSystem)) {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }
  return format(d, 'yyyy-MM-dd');
}

function itemTime(item: UnifiedEncounterItem): string {
  if (item.externalSystem && EHR_PRE_LOCALIZED_SYSTEMS.has(item.externalSystem)) {
    return formatEhrTime(item.scheduledDate);
  }
  return format(item.scheduledDate, 'h:mm a');
}

// Fixed 7-day week grouping (week view).
function groupItemsByWeek(items: UnifiedEncounterItem[], weekStart: Date): DaySchedule[] {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const groups: DaySchedule[] = [];
  for (let i = 0; i < 7; i++) {
    const date = addDays(weekStart, i);
    const dateStr = format(date, 'yyyy-MM-dd');
    groups.push({ label: dayLabels[getDay(date)], date, dateStr, items: [], isToday: dateStr === todayStr });
  }
  for (const item of items) {
    const group = groups.find(g => g.dateStr === itemDateStr(item));
    if (group) group.items.push(item);
  }
  for (const g of groups) {
    g.items.sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime());
  }
  return groups;
}

// All-time grouping: bucket every item by its own day, most-recent day first.
function groupItemsByAllDates(items: UnifiedEncounterItem[]): DaySchedule[] {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const map = new Map<string, UnifiedEncounterItem[]>();
  for (const item of items) {
    const ds = itemDateStr(item);
    if (!map.has(ds)) map.set(ds, []);
    map.get(ds)!.push(item);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dateStr, its]) => ({
      label: format(new Date(`${dateStr}T00:00:00`), 'EEEE'),
      date: new Date(`${dateStr}T00:00:00`),
      dateStr,
      items: its.sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime()),
      isToday: dateStr === todayStr,
    }));
}

// A completed encounter shows one of these, depending on its EHR delivery outcome.
const COMPLETED_STATUSES = new Set(['completed', 'sent-to-ehr', 'failed-to-send']);

function isCompleted(item: UnifiedEncounterItem): boolean {
  // Encounters mark in-flight audio as recordingStatus 'completed', which is not done yet.
  return COMPLETED_STATUSES.has(item.status) || item.hasTranscript ||
    (item._type === 'appointment' && item.recordingStatus === 'completed');
}

function computeStats(items: UnifiedEncounterItem[]): DashboardStats {
  return {
    total: items.length,
    completed: items.filter(isCompleted).length,
    pending: items.filter(i => i.status === 'scheduled' || i.status === 'in-progress').length,
  };
}

// Route the Record / Review action per item type.
function getItemAction(item: UnifiedEncounterItem, canRecord: boolean | null): { label: string; icon: typeof Mic; path: string; color: string; disabled?: boolean } | null {
  if (item.status === 'cancelled' || item.status === 'no-show') return null;
  if (item.isTranscribing) {
    return { label: 'Transcribing', icon: Clock, path: '', color: 'text-amber-700 bg-amber-50 border-amber-200', disabled: true };
  }
  const review = isCompleted(item);
  if (item._type === 'appointment') {
    if (review) {
      return { label: 'Review', icon: CheckCircle, path: `/case/${item.id}/validation`, color: 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100' };
    }
    // Only people who record get Record; the others open the appointment (AMP-136).
    return canRecord
      ? { label: 'Record', icon: Mic, path: `/recording/${item.id}`, color: 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100' }
      : { label: 'Open', icon: CalendarDays, path: `/appointments/${item.id}`, color: 'text-gray-700 bg-gray-50 border-gray-200 hover:bg-gray-100' };
  }
  return review
    ? { label: 'Review', icon: CheckCircle, path: `/encounter/${item.id}/validation`, color: 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100' }
    : { label: 'Record', icon: Mic, path: `/recording?encounterId=${item.id}`, color: 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100' };
}

// ============================================================================
// Sub-Components
// ============================================================================

function CompactHeader({ userName, weekLabel, modeLabel, onRefresh, isLoading }: {
  userName: string;
  weekLabel: string;
  modeLabel: string;
  onRefresh: () => void;
  isLoading: boolean;
}) {
  return (
    <motion.div
      className="clinical-card-static p-4 md:p-5 relative overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[rgba(30,74,122,0.03)] to-[rgba(22,160,133,0.05)]" />
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-white w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0">
            <img src="/images/amplify-favicon.png" alt="Amplify" className="w-full h-full object-contain p-1" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-display font-bold text-[var(--color-clinical-navy)]">
              Welcome back, {userName}
            </h1>
            <p className="text-xs md:text-sm text-[var(--color-clinical-blue)] opacity-80">
              {weekLabel}{modeLabel ? ` · ${modeLabel}` : ''}
            </p>
          </div>
        </div>
        <motion.button
          onClick={onRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-medium px-4 py-2.5 rounded-xl shadow-clinical-soft transition-all duration-200 disabled:opacity-50 min-h-[44px] self-start sm:self-auto"
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            animate={{ rotate: isLoading ? 360 : 0 }}
            transition={{ duration: 1, repeat: isLoading ? Infinity : 0, ease: 'linear' }}
          >
            <RefreshCw size={16} />
          </motion.div>
          <span className="text-sm">Refresh</span>
        </motion.button>
      </div>
    </motion.div>
  );
}

function StatCard({ label, value, icon: Icon, color, onClick, listNoun, delay = 0 }: {
  label: string;
  value: number;
  icon: typeof Calendar;
  color: string;
  onClick: () => void;
  listNoun: string;
  delay?: number;
}) {
  const colorMap: Record<string, { bg: string; border: string; text: string }> = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-600' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-600' },
  };
  const colors = colorMap[color] || colorMap.blue;

  return (
    <motion.button
      onClick={onClick}
      className="clinical-card p-4 md:p-5 text-left w-full cursor-pointer group"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
          <p className="stat-number text-2xl">{value}</p>
        </div>
        <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <Icon className={colors.text} size={20} />
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-[var(--color-surgical-teal)] transition-colors">
        <span>View {listNoun}</span>
        <ExternalLink size={10} />
      </div>
    </motion.button>
  );
}

function CompactWeekNav({ onPrev, onNext, onToday, isCurrentWeek, showAllTime, onToggleAllTime, weekLabel }: {
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  isCurrentWeek: boolean;
  showAllTime: boolean;
  onToggleAllTime: () => void;
  weekLabel: string;
}) {
  return (
    <motion.div
      className="bg-white rounded-xl border border-gray-200 shadow-clinical-soft p-3 md:p-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={onPrev}
          disabled={showAllTime}
          className="flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Previous week"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex items-center gap-2 flex-wrap justify-center">
          <span className="text-sm md:text-base font-semibold text-gray-800">
            {showAllTime ? 'All Time' : weekLabel}
          </span>
          {!isCurrentWeek && !showAllTime && (
            <button
              onClick={onToday}
              className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-100 min-h-[32px]"
            >
              This Week
            </button>
          )}
          <button
            onClick={onToggleAllTime}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all min-h-[32px] ${
              showAllTime
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            {showAllTime ? 'Show Weekly' : 'All Time'}
          </button>
        </div>

        <button
          onClick={onNext}
          disabled={showAllTime}
          className="flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Next week"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </motion.div>
  );
}

function ItemRow({ item }: { item: UnifiedEncounterItem }) {
  const navigate = useNavigate();
  const canRecord = useOrganizationStore((s) => s.canRecord);
  const action = getItemAction(item, canRecord);
  const time = itemTime(item);
  const isCancelled = item.status === 'cancelled' || item.status === 'no-show';

  return (
    <button
      onClick={() => { if (action && !action.disabled) navigate(action.path); }}
      disabled={!action || action.disabled}
      title={action?.disabled ? 'This encounter is being transcribed' : undefined}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all min-h-[44px] text-left ${
        isCancelled
          ? 'opacity-50 cursor-not-allowed'
          : action?.disabled
            ? 'cursor-default'
            : 'hover:bg-gray-50 active:bg-gray-100 cursor-pointer'
      }`}
    >
      <span className="text-xs font-mono text-gray-400 w-16 flex-shrink-0">{time}</span>
      <span className={`text-sm font-medium flex-1 min-w-0 truncate ${isCancelled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
        {item.patientName}
      </span>
      {item.type && (
        <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0 hidden sm:inline">
          {item.type}
        </span>
      )}
      {action && (
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border flex-shrink-0 ${action.color}`}>
          <action.icon size={12} />
          {action.label}
        </span>
      )}
    </button>
  );
}

function DayScheduleView({ daySchedules, emptyNoun }: { daySchedules: DaySchedule[]; emptyNoun: string }) {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    const todayGroup = daySchedules.find(d => d.isToday && d.items.length > 0);
    if (todayGroup) {
      initial.add(todayGroup.dateStr);
    } else {
      const firstWithItems = daySchedules.find(d => d.items.length > 0);
      if (firstWithItems) initial.add(firstWithItems.dateStr);
    }
    return initial;
  });

  const toggleDay = (dateStr: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      return next;
    });
  };

  const daysWithItems = daySchedules.filter(d => d.items.length > 0);

  if (daysWithItems.length === 0) {
    return (
      <motion.div
        className="clinical-card-static p-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <div className="text-center py-8">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
            <Calendar className="w-7 h-7 text-gray-400" />
          </div>
          <p className="text-sm font-semibold text-gray-900">No {emptyNoun} to show</p>
          <p className="text-xs text-gray-500 mt-1">Try a different week, or toggle All Time.</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="clinical-card-static overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.3 }}
    >
      <div className="px-4 md:px-5 pt-4 md:pt-5 pb-2">
        <h2 className="text-sm font-semibold text-[var(--color-clinical-navy)] flex items-center gap-2">
          <Calendar size={16} className="text-[var(--color-surgical-teal)]" />
          Schedule
        </h2>
      </div>

      <div className="divide-y divide-gray-100">
        {daySchedules.map((day) => {
          if (day.items.length === 0) return null;
          const isExpanded = expandedDays.has(day.dateStr);

          return (
            <div key={day.dateStr}>
              <button
                onClick={() => toggleDay(day.dateStr)}
                className={`w-full flex items-center justify-between px-4 md:px-5 py-3 min-h-[44px] transition-colors ${
                  day.isToday ? 'bg-blue-50/40' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronUp size={16} className="text-gray-400 rotate-180" />
                  )}
                  <span className={`text-sm font-medium ${day.isToday ? 'text-blue-700' : 'text-gray-800'}`}>
                    {day.label}, {format(day.date, 'MMM d')}
                  </span>
                  {day.isToday && (
                    <span className="text-[10px] font-medium text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                      Today
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  day.isToday ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {day.items.length}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-2 md:px-3 pb-2">
                      {day.items.map(item => (
                        <ItemRow key={item.id} item={item} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function QuickActions({ listPath, listNoun, onRefresh, isLoading }: {
  listPath: string;
  listNoun: string;
  onRefresh: () => void;
  isLoading: boolean;
}) {
  const navigate = useNavigate();

  return (
    <motion.div
      className="flex flex-col sm:flex-row gap-3"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
    >
      <motion.button
        onClick={() => navigate(listPath)}
        className="flex-1 flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-[var(--color-clinical-navy)] font-medium px-5 py-3 rounded-xl shadow-clinical-soft transition-all duration-200 min-h-[44px]"
        whileTap={{ scale: 0.98 }}
      >
        <ExternalLink size={16} />
        <span className="text-sm">View All {listNoun}</span>
      </motion.button>

      <motion.button
        onClick={onRefresh}
        disabled={isLoading}
        className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-medium px-5 py-3 rounded-xl shadow-clinical-soft transition-all duration-200 disabled:opacity-50 min-h-[44px]"
        whileTap={{ scale: 0.95 }}
      >
        <motion.div
          animate={{ rotate: isLoading ? 360 : 0 }}
          transition={{ duration: 1, repeat: isLoading ? Infinity : 0, ease: 'linear' }}
        >
          <RefreshCw size={16} />
        </motion.div>
        <span className="text-sm">Refresh</span>
      </motion.button>
    </motion.div>
  );
}

// ============================================================================
// Skeleton Loading
// ============================================================================

function DashboardSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-xl" />
          <div className="space-y-2 flex-1">
            <div className="h-5 bg-gray-200 rounded w-48" />
            <div className="h-3 bg-gray-100 rounded w-36" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="space-y-2">
              <div className="h-3 bg-gray-100 rounded w-20" />
              <div className="h-6 bg-gray-200 rounded w-10" />
              <div className="h-2 bg-gray-100 rounded w-24 mt-2" />
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
        <div className="space-y-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 h-11 px-3">
              <div className="w-14 h-3 bg-gray-100 rounded" />
              <div className="flex-1 h-3 bg-gray-200 rounded w-32" />
              <div className="w-16 h-6 bg-gray-100 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Provider Homepage (both modes)
// ============================================================================

const ConnectedModeDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { dashboardMode, loading: capsLoading } = useCapabilities();
  const isConnected = dashboardMode === 'encounters-only';
  const listNoun = isConnected ? 'Encounters' : 'Appointments';
  const listPath = isConnected ? '/encounters' : '/appointments';
  // "Standalone" is our internal term; a practice does not need a mode label.
  const modeLabel = isConnected ? 'Connected Mode' : '';

  const { fetchSubscription, fetchPersonalUsage } = useSubscriptionStore();
  const [items, setItems] = useState<UnifiedEncounterItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  // Standalone defaults to All Time (scheduled appointments are often sparse, so an
  // empty current week would read as "broken"). Connected defaults to the week view.
  const [showAllTime, setShowAllTime] = useState(false);
  const allTimeInit = useRef(false);
  useEffect(() => {
    if (!capsLoading && !allTimeInit.current) {
      allTimeInit.current = true;
      if (dashboardMode === 'standard') setShowAllTime(true);
    }
  }, [capsLoading, dashboardMode]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let dateFrom: string | undefined;
      let dateTo: string | undefined;
      if (!showAllTime) {
        dateFrom = startOfWeek(selectedWeek, { weekStartsOn: 0 }).toISOString();
        dateTo = endOfWeek(selectedWeek, { weekStartsOn: 0 }).toISOString();
      }

      if (isConnected) {
        const params: Record<string, unknown> = { limit: 200 };
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        const response = await encounterService.getEncounters(params as any);
        setItems(adaptEncounters(response.encounters || []));
      } else {
        // The appointments query endpoint caps limit at 100 (appointmentSchemas.query);
        // requesting more returns 400. The connected/encounters endpoint has no such cap.
        const response = await appointmentService.getAppointments({ limit: 100, dateFrom, dateTo });
        setItems(adaptAppointments(response.appointments || []));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to load ${listNoun.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, showAllTime, isConnected, listNoun]);

  useEffect(() => {
    if (!capsLoading) fetchItems();
  }, [capsLoading, fetchItems]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') {
      fetchSubscription();
      fetchPersonalUsage();
    }
  }, [user?.role]);

  const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 0 });
  const isCurrentWeek =
    format(weekStart, 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 0 }), 'yyyy-MM-dd');
  const weekLabel = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;

  const daySchedules = useMemo(
    () => (showAllTime ? groupItemsByAllDates(items) : groupItemsByWeek(items, weekStart)),
    [items, weekStart, showAllTime]
  );

  const stats = useMemo(() => computeStats(items), [items]);

  if ((loading || capsLoading) && items.length === 0) {
    return <DashboardSkeleton />;
  }

  if (error && items.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-red-800 mb-2">Error loading {listNoun.toLowerCase()}</h3>
        <p className="text-sm text-red-700 mb-4">{error}</p>
        <button
          onClick={fetchItems}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-xl transition-colors min-h-[44px]"
        >
          <RefreshCw size={16} />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CompactHeader
        userName={user?.firstName || 'Doctor'}
        weekLabel={showAllTime ? 'All Time' : weekLabel}
        modeLabel={modeLabel}
        onRefresh={fetchItems}
        isLoading={loading}
      />

      <SubscriptionStatusBar />
      <DataRetentionBanner />

      {/* Stat Cards — clickable, navigate to the list for this mode */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label={`Total ${listNoun}`} value={stats.total} icon={Calendar} color="blue" listNoun={listNoun.toLowerCase()} onClick={() => navigate(listPath)} delay={0.1} />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle} color="emerald" listNoun={listNoun.toLowerCase()} onClick={() => navigate(`${listPath}?status=completed`)} delay={0.15} />
        <StatCard label="Pending" value={stats.pending} icon={Clock} color="amber" listNoun={listNoun.toLowerCase()} onClick={() => navigate(`${listPath}?status=scheduled`)} delay={0.2} />
      </div>

      {/* Week Navigation */}
      <CompactWeekNav
        onPrev={() => setSelectedWeek(subWeeks(selectedWeek, 1))}
        onNext={() => setSelectedWeek(addWeeks(selectedWeek, 1))}
        onToday={() => setSelectedWeek(new Date())}
        isCurrentWeek={isCurrentWeek}
        showAllTime={showAllTime}
        onToggleAllTime={() => setShowAllTime(!showAllTime)}
        weekLabel={weekLabel}
      />

      {/* Schedule / worklist (both week and All Time) */}
      <DayScheduleView daySchedules={daySchedules} emptyNoun={listNoun.toLowerCase()} />

      {/* Quick Actions */}
      <QuickActions listPath={listPath} listNoun={listNoun} onRefresh={fetchItems} isLoading={loading} />
    </div>
  );
};

export default ConnectedModeDashboard;
